import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowLeft, Clock, Video, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PitchSessions() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const tabActive = isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white';
  const tabInactive = isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300';

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/pitch-sessions/my-sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: <Clock size={12} /> },
      'confirmed': { bg: 'bg-green-100 text-green-800', icon: <Check size={12} /> },
      'cancelled': { bg: 'bg-red-100 text-red-800', icon: <X size={12} /> },
      'completed': { bg: 'bg-blue-100 text-blue-800', icon: <Video size={12} /> }
    };
    const badge = badges[status] || badges['pending'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg}`}>
        {badge.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const now = new Date();
  const upcomingSessions = sessions.filter(s => new Date(s.scheduled_at) > now && s.status === 'confirmed');
  const pendingSessions = sessions.filter(s => s.status === 'pending');
  const pastSessions = sessions.filter(s => new Date(s.scheduled_at) <= now || s.status === 'completed' || s.status === 'cancelled');

  const currentList = activeTab === 'upcoming' ? upcomingSessions : activeTab === 'pending' ? pendingSessions : pastSessions;

  return (
    <div className={`min-h-screen ${bg}`} data-testid="pitch-sessions-page">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <Link to="/" className={`inline-flex items-center gap-2 ${textSecondary} hover:${textPrimary} mb-4`}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <h1 className={`text-3xl font-bold ${textPrimary}`}>Pitch Sessions</h1>
          <p className={textSecondary}>Manage your pitch meetings with investors.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'upcoming' ? tabActive : tabInactive}`}
          >
            <Calendar size={16} className="inline mr-2" />
            Upcoming ({upcomingSessions.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'pending' ? tabActive : tabInactive}`}
          >
            <AlertCircle size={16} className="inline mr-2" />
            Pending ({pendingSessions.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'past' ? tabActive : tabInactive}`}
          >
            <Clock size={16} className="inline mr-2" />
            Past ({pastSessions.length})
          </button>
        </div>

        {loading ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <p className={textSecondary}>Loading...</p>
          </Card>
        ) : currentList.length === 0 ? (
          <Card className={`p-8 text-center ${cardBg}`}>
            <Calendar size={48} className={`mx-auto mb-4 ${textMuted}`} />
            <h3 className={`text-lg font-semibold mb-2 ${textPrimary}`}>
              {activeTab === 'upcoming' ? 'No upcoming sessions' : activeTab === 'pending' ? 'No pending requests' : 'No past sessions'}
            </h3>
            <p className={`mb-4 ${textSecondary}`}>
              {activeTab === 'upcoming' 
                ? 'Schedule pitch sessions with your matched investors.' 
                : activeTab === 'pending'
                  ? 'Session requests from matches will appear here.'
                  : 'Your completed and cancelled sessions will appear here.'}
            </p>
            <Link to="/matches">
              <Button className="btn-primary">
                View Matches
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {currentList.map((session) => (
              <Card key={session.session_id} className={`p-4 ${cardBg}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className={`font-semibold ${textPrimary}`}>
                        {user?.role === 'founder' ? session.investor_name : session.founder_name}
                      </h3>
                      {getStatusBadge(session.status)}
                    </div>
                    <div className={`flex items-center gap-4 text-sm ${textMuted}`}>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(session.scheduled_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatTime(session.scheduled_at)}
                      </span>
                    </div>
                    {session.notes && (
                      <p className={`mt-2 text-sm ${textSecondary}`}>{session.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {session.status === 'confirmed' && session.meeting_link && (
                      <a href={session.meeting_link} target="_blank" rel="noopener noreferrer">
                        <Button className="btn-primary" size="sm">
                          <Video size={14} className="mr-1" />
                          Join
                        </Button>
                      </a>
                    )}
                    {session.status === 'pending' && user?.role === 'investor' && (
                      <Button variant="outline" size="sm" className="text-green-600 border-green-600">
                        <Check size={14} className="mr-1" />
                        Confirm
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
